import { useState, useEffect, useCallback, useRef } from 'react';
import { Flex, Heading, Button, Divider, useToasts } from '@applyboard/crystal-ui';
import { StudentOutlineIcon } from '@applyboard/ui-icons';
import { formatDateToPopulateDatePicker, formatMonthYear } from '~/utils/formatDate';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import { resolveAttributeValue } from '~/utils/transcriptExtraction';

import { Card } from '../Card';
import { AttachedDocuments } from '../shared/AttachedDocuments';
import { useSchoolCustomizations } from '~/data/useSchoolCustomizations';
import { GpaCalculator } from './components/GpaCalculator';
import { TranscriptExtraction } from './components/TranscriptExtraction';
import { TranscriptDocument } from '~/types/transcriptExtraction';
import {
  EducationHistoryEditFields,
  EducationHistoryEditData,
  getChangedMetadata,
} from './components/EducationHistoryEditFields';
import { AddEducationHistoryDialog } from './components/AddEducationHistoryDialog';
import { RelevantStudentInformation } from './components/RelevantStudentInformation';
import { useUpdateEducationHistoryMetadata } from '../../../data/useUpdateEducationHistory';
import { useTranscriptAiEnabled, useGradeSchemeRequired } from '../../../data/useGetCustomizedDocumentTypes';
import { validateEducationHistory } from './educationHistory.validation';
import { EducationHistory as EducationHistoryType } from '~/data/useApplication';

function getLatestExtraction(eh: EducationHistoryType): TranscriptDocument | undefined {
  const extractions = (eh.transcriptExtractions ?? []) as TranscriptDocument[];
  if (extractions.length === 0) return undefined;
  return extractions.reduce<TranscriptDocument | undefined>((latest, current) => {
    const latestTime = latest
      ? new Date(
          latest.attributes.extractedOn ?? latest.attributes.updatedAt ?? latest.attributes.createdAt ?? 0
        ).getTime()
      : -Infinity;
    const currentTime = new Date(
      current.attributes.extractedOn ?? current.attributes.updatedAt ?? current.attributes.createdAt ?? 0
    ).getTime();
    return currentTime > latestTime ? current : latest;
  }, undefined);
}

/** Build initial edit data from the education history item and its transcript extraction */
function buildEditData(eh: EducationHistoryType, extraction?: TranscriptDocument): EducationHistoryEditData {
  const attrs = extraction?.attributes;
  return {
    schoolName: (attrs && resolveAttributeValue(attrs.schoolName)) || eh.schoolName || '',
    educationLevel: eh.level.code || '',
    educationCountry: eh.country.alpha2Code || '',
    credentialReceived: (attrs && resolveAttributeValue(attrs.credentialReceived)) || eh.credentialReceived || '',
    fieldOfStudy: (attrs && resolveAttributeValue(attrs.fieldOfStudy)) || '',
    degree: (attrs && resolveAttributeValue(attrs.degree)) || '',
    gradeSchemeId: (attrs && resolveAttributeValue(attrs.gradeSchemeId)) || '',
    gradeSchemeName: (attrs && resolveAttributeValue(attrs.gradeSchemeName)) || '',
    startDate: formatDateToPopulateDatePicker(eh.startDate),
    endDate: formatDateToPopulateDatePicker(eh.endDate),
  };
}

export function EducationHistory() {
  const {
    application: { educationHistories, studentPersonaInformation, id: applicationId, gpa, gpaCalculationParameters },
    courses,
    educationHistoryTimestamps,
    ehKeyTimestamps,
  } = useAppProcessing();

  const { schoolCustomizations } = useSchoolCustomizations();
  const enableGpaCalculation = schoolCustomizations?.schoolCustomization.enableGpaCalculation ?? false;
  const shouldRenderGpaCalculator = enableGpaCalculation && courses.length > 0;

  return (
    <Card id="educationHistory" icon={StudentOutlineIcon} title="Education History">
      <Flex justify="start" direction="column" gap={14}>
        {educationHistories.educationHistoryItems.map((eh, index) => (
          <Flex key={eh.key} direction="column" gap={14}>
            {index > 0 && <Divider m={0} />}
            <EducationHistoryItem
              educationHistoryItem={eh}
              applicationId={applicationId}
              studentId={studentPersonaInformation?.id}
              educationHistoryTimestamps={educationHistoryTimestamps}
              ehKeyTimestamps={ehKeyTimestamps}
            />
          </Flex>
        ))}
        {studentPersonaInformation?.id && (
          <>
            <Divider m={0} />
            <AddEducationHistoryDialog applicationId={applicationId} studentId={studentPersonaInformation.id} />
          </>
        )}
        <RelevantStudentInformation
          schoolInformation={educationHistories.schoolInformation}
          studentId={studentPersonaInformation?.id}
          applicationId={applicationId}
        />
        {shouldRenderGpaCalculator && (
          <GpaCalculator
            courses={courses}
            applicationId={applicationId}
            gpa={gpa}
            gpaCalculationParameters={gpaCalculationParameters}
          />
        )}
      </Flex>
    </Card>
  );
}

type EducationHistoryItemProps = Readonly<{
  educationHistoryItem: EducationHistoryType;
  applicationId: string;
  studentId?: string;
  educationHistoryTimestamps?: Record<string, { updatedAt: string; updatedByRole?: string }>;
  ehKeyTimestamps?: Record<string, { updatedAt: string; updatedByRole?: string }>;
}>;

function EducationHistoryItem({
  educationHistoryItem: eh,
  applicationId,
  studentId,
  educationHistoryTimestamps,
  ehKeyTimestamps,
}: EducationHistoryItemProps) {
  const toast = useToasts();
  const { updateEducationHistoryMetadata, isUpdatingEducationHistoryMetadata } = useUpdateEducationHistoryMetadata();
  const transcriptAiEnabled = useTranscriptAiEnabled();
  const gradeSchemeRequired = useGradeSchemeRequired();

  const latestExtraction = getLatestExtraction(eh);
  const originalData = useRef<EducationHistoryEditData>(buildEditData(eh, latestExtraction));
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<EducationHistoryEditData>(() => buildEditData(eh, latestExtraction));

  // Sync props to state when not editing (e.g., after a save from the Evaluation tab)
  useEffect(() => {
    if (!isEditing) {
      const fresh = buildEditData(eh, latestExtraction);
      originalData.current = fresh;
      setEditedData(fresh);
    }
  }, [eh, isEditing, latestExtraction]);

  const handleFieldChange = useCallback((field: keyof EducationHistoryEditData, value: string) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleCancel = useCallback(() => {
    setEditedData(buildEditData(eh, latestExtraction));
    setIsEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eh]);

  const handleSave = useCallback(async () => {
    const validation = validateEducationHistory(editedData);
    if (!validation.isValid && validation.errors) {
      validation.errors.forEach((error) => {
        toast.negative(new Error(error));
      });
      return;
    }

    if (!studentId) {
      toast.negative(new Error('Student ID is not available'));
      return;
    }

    const changedMetadata = getChangedMetadata(originalData.current, editedData);
    if (!changedMetadata) {
      setIsEditing(false);
      return;
    }

    const educationHistoryId = latestExtraction?.id ?? eh.documents[0]?.key;

    try {
      await updateEducationHistoryMetadata({
        studentId,
        educationHistoryId,
        applicationId,
        educationHistoryKey: eh.key,
        metadata: changedMetadata,
      });
      toast.positive('Education history updated successfully');
      setIsEditing(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update education history';
      toast.negative(new Error(errorMessage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedData, studentId, latestExtraction, eh.key, applicationId]);

  return (
    <Flex justify="start" direction="column" gap={8}>
      <Flex direction="column" gap={6}>
        <Flex justify="between" align="center">
          <Heading level={4}>{eh.schoolName || 'Education History'}</Heading>
          {!isEditing && (
            <Button size="sm" variant="outlined" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}
        </Flex>

        {isEditing ? (
          <Flex direction="column" gap={6}>
            <EducationHistoryEditFields
              editedData={editedData}
              onChange={handleFieldChange}
              gradeScheme={{ enabled: transcriptAiEnabled, required: gradeSchemeRequired }}
            />
            <Flex gap={4} justify="end">
              <Button size="sm" variant="outlined" onClick={handleCancel} disabled={isUpdatingEducationHistoryMetadata}>
                Cancel
              </Button>
              <Button size="sm" intent="primary" onClick={handleSave} loading={isUpdatingEducationHistoryMetadata}>
                Save
              </Button>
            </Flex>
          </Flex>
        ) : (
          <>
            {(() => {
              const docId = latestExtraction?.id ?? eh.documents?.[0]?.key;
              const ehTimestamp =
                (docId ? educationHistoryTimestamps?.[docId] : undefined) ?? ehKeyTimestamps?.[eh.key];

              return latestExtraction ? (
                <TranscriptExtraction
                  key={latestExtraction.id}
                  document={latestExtraction}
                  updatedAt={ehTimestamp?.updatedAt}
                  updatedByRole={ehTimestamp?.updatedByRole}
                  manualFields={{
                    credentialReceived: eh.credentialReceived,
                    educationLevel: eh.level.name,
                    educationCountry: eh.country.countryLabel,
                    startDate: formatMonthYear(eh.startDate),
                    endDate: formatMonthYear(eh.endDate),
                  }}
                />
              ) : (
                <TranscriptExtraction
                  updatedAt={ehTimestamp?.updatedAt}
                  updatedByRole={ehTimestamp?.updatedByRole}
                  manualFields={{
                    credentialReceived: eh.credentialReceived,
                    educationLevel: eh.level.name,
                    educationCountry: eh.country.countryLabel,
                    startDate: formatMonthYear(eh.startDate),
                    endDate: formatMonthYear(eh.endDate),
                  }}
                />
              );
            })()}
          </>
        )}
      </Flex>

      <AttachedDocuments
        showTitle
        attachmentDocuments={eh.documents}
        section="educationHistory"
        sectionReference={eh.key}
      />
    </Flex>
  );
}
